import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { apiFetch } from '@/data/api';
import { getInterviewStyles, getOutstandingGaps } from '@/data/client';
import { ADMIN_CLIENTS_KEY, getAdminClients } from '@/components/admin/data';
import { ConnChip } from '@/components/admin/ConnChip';
import { apiErrMsg } from '@/components/admin/util';
import { ProfileForm } from '@/components/admin/ProfileForm';
import { LeadsForm } from '@/components/admin/LeadsForm';
import { PpcForm } from '@/components/admin/PpcForm';
import { TrafficForm } from '@/components/admin/TrafficForm';
import { ContentForm } from '@/components/admin/ContentForm';
import { TeamTritonForm } from '@/components/admin/TeamTritonForm';
import { DemoForm } from '@/components/admin/DemoForm';
import { ReferralCreditForm } from '@/components/admin/ReferralCreditForm';
import { LegalPagesPanel } from '@/components/admin/LegalPagesPanel';
import { SitemapRegistryPanel } from '@/components/admin/SitemapRegistryPanel';
import { AssistantTranscripts } from '@/components/admin/AssistantTranscripts';
import { ConversationForm } from '@/components/admin/ConversationForm';
import { LeadCaptureSection } from '@/components/admin/LeadCaptureSection';
import { SignalReturnSection } from '@/components/admin/SignalReturnSection';
import { MonthlyReportSection } from '@/components/admin/MonthlyReportSection';
import { ClientUsersEditor } from '@/components/admin/ClientUsersEditor';
import { DeleteClientModal } from '@/components/admin/DeleteClientModal';

/* One client, full screen, one tab per concern, one Save per form.

   The settings dialog this replaces held roughly forty fields and six Save
   buttons at different scroll depths. Each tab now groups the fields a person
   is actually working on — everything campaignIQ reads sits under PPC, with
   Signal Return beneath it — and saves only those fields, against the PATCH
   the Worker gained for exactly this (worker/client-connections.ts). Profile is
   the one tab that still sends every field, because its endpoint is not a
   PATCH yet.

   Tabs unmount when switched away from, so an unsaved edit would vanish
   without a word. Every form reports its dirtiness under its own id — a tab
   can hold two forms, and one going clean must not hide the other — and a
   switch or reload is refused while any is dirty. */

const TABS = [
  { value: 'profile', label: 'Profile' },
  { value: 'leads', label: 'Leads' },
  { value: 'ppc', label: 'PPC' },
  { value: 'traffic', label: 'Traffic & Reviews' },
  { value: 'content', label: 'Content' },
  { value: 'users', label: 'Team & Users' },
  { value: 'legal', label: 'Legal pages' },
  { value: 'assistant', label: 'Assistant' },
  { value: 'conversation', label: 'conversationIQ' },
  { value: 'advanced', label: 'Advanced' },
];
const TAB_VALUES = new Set(TABS.map((t) => t.value));
// Links minted before this tab set existed keep landing somewhere sensible.
const TAB_ALIASES: Record<string, string> = { settings: 'profile', signal: 'ppc', report: 'advanced', danger: 'advanced' };

export function AdminClientPage() {
  const { orgId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const rawTab = params.get('tab') ?? 'profile';
  const tab = TAB_VALUES.has(rawTab) ? rawTab : (TAB_ALIASES[rawTab] ?? 'profile');

  const { data, isLoading, isError, error } = useQuery({ queryKey: ADMIN_CLIENTS_KEY, queryFn: getAdminClients });
  const { data: styleData } = useQuery({ queryKey: ['admin', 'interview-styles'], queryFn: getInterviewStyles });
  const client = data?.clients.find((c) => c.orgId === orgId);
  const managers = data?.managers ?? [];
  const managerName = client ? managers.find((m) => m.id === client.contentManagerId)?.name : undefined;
  const styles = styleData?.styles ?? [];

  /* Dirty ids, not a boolean: the Leads tab holds a form and a section, the
     Advanced tab two forms. Returning the same Set when nothing changes keeps
     the forms' report effects from re-rendering in a loop. */
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(() => new Set());
  const onDirtyChange = useCallback((id: string, d: boolean) => {
    setDirtyIds((prev) => {
      if (prev.has(id) === d) return prev;
      const next = new Set(prev);
      if (d) next.add(id); else next.delete(id);
      return next;
    });
  }, []);
  const dirty = dirtyIds.size > 0;
  const onSaved = useCallback(() => { qc.invalidateQueries({ queryKey: ADMIN_CLIENTS_KEY }); }, [qc]);

  const switchTab = (next: string) => {
    if (next === tab) return;
    if (dirty && !window.confirm('You have unsaved changes on this tab. Discard them?')) return;
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    });
  };

  useEffect(() => {
    if (!dirty) return;
    // preventDefault is the standard; returnValue is what Chrome before 119 honours.
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: () => apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}`, { method: 'DELETE' }),
    onMutate: () => setDelErr(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_CLIENTS_KEY });
      navigate('/admin');
    },
    onError: (e) => setDelErr(apiErrMsg(e, 'Could not delete this client.')),
  });

  if (isLoading) {
    return <div className="dash-stack"><div className="lead-none">Loading client…</div></div>;
  }
  if (isError || !data) {
    return (
      <div className="dash-stack">
        <Link className="cq-back" to="/admin"><Icon name="chevron-left" size={16} /> Back to clients</Link>
        <div className="lead-none">Couldn’t load clients: {error instanceof Error ? error.message : 'error'}</div>
      </div>
    );
  }
  if (!client) {
    return (
      <div className="dash-stack">
        <Link className="cq-back" to="/admin"><Icon name="chevron-left" size={16} /> Back to clients</Link>
        <div className="lead-none">No client with that id. It may have been deleted, or the link is from a different Clerk instance.</div>
      </div>
    );
  }

  const form = { client, onSaved, onDirtyChange };

  return (
    <div className="dash-stack admin-client">
      <Link className="cq-back" to="/admin"><Icon name="chevron-left" size={16} /> Back to clients</Link>

      <Card variant="dark" padding="lg" className="cq-hero">
        <div className="cq-hero__txt">
          <span className="cq-hero__eyebrow"><Icon name="shield-check" size={14} /> Client settings</span>
          <h2>{client.name}</h2>
          <p>
            {client.leadCount.toLocaleString()} lead{client.leadCount === 1 ? '' : 's'}
            {managerName ? ` · ${managerName}` : ''}
            {client.isDemo ? ' · demo account' : ''}
          </p>
        </div>
        {client.configured
          ? <Badge variant="success" dot>Connected</Badge>
          : <Badge variant="neutral">Not configured</Badge>}
      </Card>

      <div className="admin-conns admin-client__chips">
        <ConnChip label="BigQuery" on={!!client.bqTable} detail={client.bqTable} />
        <ConnChip label="Direct capture" on={client.leadCaptureEnabled} detail={client.leadCaptureEnabled ? 'Wufoo / Formspree' : null} />
        <ConnChip label="Google Ads" on={!!client.googleAdsCustomerId} detail={client.googleAdsCustomerId} />
        <ConnChip label="GA4" on={!!client.ga4PropertyId} detail={client.ga4PropertyId} />
        <ConnChip label="Search Console" on={!!client.gscSiteUrl} detail={client.gscSiteUrl} />
        <ConnChip label="Reviews" on={!!client.gatherupClientId} detail={client.gatherupClientId} />
        <ConnChip label="AI interview" on={client.aiInterviewEnabled} detail={client.aiInterviewEnabled ? client.interviewStyle : null} />
        <ConnChip label="Expert Studio" on={client.selfServiceEnabled} detail={null} />
        <OpenQuestionsChip orgId={client.orgId} />
      </div>

      <div className="lead-tabsbar admin-client__tabs">
        <Tabs items={TABS} value={tab} onChange={switchTab} variant="pill" />
      </div>

      <Card padding="lg" className="admin-client__panel">
        {tab === 'profile' && <ProfileForm {...form} />}
        {tab === 'leads' && (
          <>
            <LeadsForm {...form} />
            <LeadCaptureSection orgId={client.orgId} />
          </>
        )}
        {tab === 'ppc' && (
          <>
            <PpcForm {...form} />
            <SignalReturnSection orgId={client.orgId} />
          </>
        )}
        {tab === 'traffic' && <TrafficForm {...form} />}
        {tab === 'content' && (
          <>
            <ContentForm {...form} styles={styles} />
            <SitemapRegistryPanel orgId={client.orgId} />
          </>
        )}
        {tab === 'users' && (
          <>
            <TeamTritonForm {...form} managers={managers} />
            <ClientUsersEditor orgId={client.orgId} />
          </>
        )}
        {tab === 'legal' && <LegalPagesPanel client={client} onDirtyChange={onDirtyChange} />}
        {tab === 'assistant' && <AssistantTranscripts orgId={orgId} />}
        {tab === 'conversation' && <ConversationForm client={client} onDirtyChange={onDirtyChange} />}
        {tab === 'advanced' && (
          <>
            <DemoForm {...form} />
            <ReferralCreditForm orgId={client.orgId} />
            <MonthlyReportSection orgId={client.orgId} />
            <section className="admin-sect">
              <h4 className="admin-sect__title">Delete this client</h4>
              <p className="dash-muted admin-sect__note">
                Removes the client and everything stored for it in tritonIQ — leads, content, vault sources, galleries, settings. The Clerk organization and its users are removed too. This cannot be undone.
              </p>
              <Button variant="danger" size="sm" iconLeft={<Icon name="trash" size={14} />} onClick={() => setConfirmDelete(true)}>
                Delete {client.name}…
              </Button>
            </section>
          </>
        )}
      </Card>

      {confirmDelete && (
        <DeleteClientModal
          client={client}
          busy={del.isPending}
          error={delErr}
          onCancel={() => { setConfirmDelete(false); setDelErr(null); }}
          onConfirm={() => del.mutate()}
        />
      )}
    </div>
  );
}

/* "Are we waiting on this client for anything?" — answered next to the
   connection chips, before a call. Hidden when nothing is open. */
function OpenQuestionsChip({ orgId }: { orgId: string }) {
  const { data } = useQuery({ queryKey: ['admin', 'gaps', 'outstanding'], queryFn: getOutstandingGaps, staleTime: 60_000 });
  const mine = (data?.items ?? []).filter((s) => s.clientId === orgId);
  if (!mine.length) return null;
  const oldest = mine.reduce((a, b) => (a.sentAt < b.sentAt ? a : b));
  const days = Math.max(0, Math.floor((Date.now() - Date.parse(oldest.sentAt.replace(' ', 'T') + (oldest.sentAt.endsWith('Z') ? '' : 'Z'))) / 86_400_000));
  return <ConnChip label="Client questions" on detail={`${mine.length} set${mine.length === 1 ? '' : 's'} open · oldest ${days}d · ${oldest.short}`} />;
}
